import React, { useState, useEffect, useRef } from "react";
import { useQuote } from "../context/QuoteContext";
import api from "../api";
import { formatEur, freeRitiroPeriods } from "../utils";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Plus, Minus, Lock, Unlock, MapPin, Truck, Loader2, ChevronDown, Sparkles, Gift } from "lucide-react";
import { toast } from "sonner";

const DEPOT = "Via Cascina Bellaria 18, Buccinasco, MI";

export default function RitiroSection() {
  const { ritiro, setRitiro, ritiroTotal, ritiroBreakdown, settings, effectiveGaugePeriods } = useQuote();
  const [open, setOpen] = useState(true);
  const [capLoading, setCapLoading] = useState(false);
  const [manualCosto, setManualCosto] = useState("");
  const [manualFach, setManualFach] = useState("");

  const lastAuto = useRef("");
  const autoTimer = useRef(null);

  const upd = (patch) => setRitiro((r) => ({ ...r, ...patch }));

  const freePeriods = Array.isArray(ritiro.freePeriods)
    ? ritiro.freePeriods.map(Number)
    : (ritiro.freePeriod != null && ritiro.freePeriod !== "" ? [Number(ritiro.freePeriod)] : []);
  const toggleFreePeriod = (m) => {
    const cur = freePeriods;
    const next = cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m].sort((a, b) => a - b);
    upd({ freePeriods: next });
  };

  const lookupCap = async () => {
    const cap = (ritiro.cap || "").trim();
    if (cap.length < 5) return;
    lastAuto.current = `cap:${cap}`;
    setCapLoading(true);
    try {
      let res;
      try {
        res = (await api.post("/ai/lookup-cap", { cap })).data;
      } catch {
        res = (await api.post("/ai/estimate-travel", { cap, citta: ritiro.citta })).data;
      }
      upd({
        citta: res.citta || ritiro.citta,
        distanzaKm: ritiro.isDistanceManual ? ritiro.distanzaKm : (res.distanzaKm ?? ritiro.distanzaKm),
        tempoMin: ritiro.isTimeManual ? ritiro.tempoMin : (res.tempoMin ?? ritiro.tempoMin),
      });
      toast.success(`Località trovata: ${res.citta || cap}`);
    } catch {
      toast.error("Impossibile calcolare la distanza per questo CAP.");
    } finally {
      setCapLoading(false);
    }
  };

  const lookupZone = async () => {
    const citta = (ritiro.citta || "").trim();
    if (citta.length < 3) return;
    lastAuto.current = `citta:${citta}`;
    setCapLoading(true);
    try {
      const res = (await api.post("/ai/estimate-travel", { cap: ritiro.cap || "", citta })).data;
      upd({
        citta: res.citta || citta,
        distanzaKm: ritiro.isDistanceManual ? ritiro.distanzaKm : (res.distanzaKm ?? ritiro.distanzaKm),
        tempoMin: ritiro.isTimeManual ? ritiro.tempoMin : (res.tempoMin ?? ritiro.tempoMin),
      });
      toast.success(`Stima AI aggiornata per ${res.citta || citta}`);
    } catch {
      toast.error("Impossibile stimare la distanza per questa zona.");
    } finally {
      setCapLoading(false);
    }
  };

  const mapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(settings?.depotAddress || DEPOT)}&destination=${encodeURIComponent([ritiro.citta, ritiro.cap].filter(Boolean).join(" ") || "Milano")}`;

  // Auto-calculate distance & time via AI when a CAP or Zona is available
  useEffect(() => {
    if (!ritiro.enabled) return;
    if (ritiro.isDistanceManual && ritiro.isTimeManual) return;
    const cap = (ritiro.cap || "").trim();
    const citta = (ritiro.citta || "").trim();
    const hasCap = cap.length === 5;
    const hasCitta = citta.length >= 3;
    if (!hasCap && !hasCitta) return;
    const key = hasCap ? `cap:${cap}` : `citta:${citta}`;
    if (key === lastAuto.current) return;
    if (autoTimer.current) clearTimeout(autoTimer.current);
    autoTimer.current = setTimeout(() => { if (hasCap) lookupCap(); else lookupZone(); }, 900);
    return () => autoTimer.current && clearTimeout(autoTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ritiro.cap, ritiro.citta, ritiro.enabled, ritiro.isDistanceManual, ritiro.isTimeManual]);

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid="ritiro-section">
      <div className="flex items-center justify-between p-6 pb-4">
        <div className="flex items-center gap-3">
          <Truck className="h-5 w-5 text-[#1a5f5a]" />
          <h3 className="font-display font-semibold text-lg">Ritiro a domicilio</h3>
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={ritiro.enabled} onCheckedChange={(v) => upd({ enabled: v })} data-testid="ritiro-toggle" />
          <button onClick={() => setOpen(!open)} className="text-slate-400"><ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} /></button>
        </div>
      </div>

      {open && ritiro.enabled && (
        <div className="px-6 pb-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">CAP</Label>
              <div className="flex gap-2 mt-1">
                <Input value={ritiro.cap} maxLength={5} onChange={(e) => upd({ cap: e.target.value.replace(/\D/g, "") })}
                  onBlur={lookupCap} onKeyDown={(e) => e.key === "Enter" && lookupCap()} placeholder="20090" data-testid="ritiro-cap-input" />
                <Button variant="outline" size="icon" onClick={lookupCap} disabled={capLoading} data-testid="ritiro-cap-lookup">
                  {capLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
                </Button>
              </div>
              <a href={mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="ritiro-maps-button"
                className="inline-flex items-center gap-1.5 mt-2 h-8 px-2.5 rounded-md border border-slate-200 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                <MapPin className="h-3.5 w-3.5" /> Apri in Maps
              </a>
            </div>
            <div>
              <Label className="text-xs">Città / Zona</Label>
              <div className="flex gap-2 mt-1">
                <Input value={ritiro.citta} onChange={(e) => upd({ citta: e.target.value })}
                  onBlur={lookupZone}
                  onKeyDown={(e) => e.key === "Enter" && lookupZone()}
                  placeholder="Milano" data-testid="ritiro-citta-input" />
                <Button variant="outline" size="icon" onClick={lookupZone} disabled={capLoading} data-testid="ritiro-zone-lookup" title="Stima AI da zona">
                  {capLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>

          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2" data-testid="ritiro-free-row">
            <label className="flex items-center gap-2 text-sm text-emerald-800 cursor-pointer">
              <Gift className="h-4 w-4" /> Ritiro a domicilio gratuito
              <Switch checked={!!ritiro.freeEnabled}
                onCheckedChange={(v) => upd(v && freePeriods.length === 0 ? { freeEnabled: true, freePeriods: [12] } : { freeEnabled: v })}
                data-testid="ritiro-free-toggle" />
            </label>
            {ritiro.freeEnabled && (
              <div className="flex items-center gap-2 flex-wrap mt-2">
                <span className="text-xs text-emerald-700">per impegni minimi (selezione multipla):</span>
                {effectiveGaugePeriods.map((p) => {
                  const sel = freePeriods.includes(p.months);
                  return (
                    <button key={p.months} type="button" onClick={() => toggleFreePeriod(p.months)}
                      data-testid={`ritiro-free-period-${p.months}`}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${sel ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-100"}`}>
                      {p.months} {p.months === 1 ? "mese" : "mesi"}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs flex items-center gap-1">Distanza (km)
                <button onClick={() => upd({ isDistanceManual: !ritiro.isDistanceManual })}>{ritiro.isDistanceManual ? <Lock className="h-3 w-3 text-[#1a5f5a]" /> : <Unlock className="h-3 w-3 text-slate-300" />}</button>
              </Label>
              <Input type="number" value={ritiro.distanzaKm} onChange={(e) => upd({ distanzaKm: parseFloat(e.target.value) || 0, isDistanceManual: true })} className="mt-1" data-testid="ritiro-distanza-input" />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1">Tempo (min, sola andata)
                <button onClick={() => upd({ isTimeManual: !ritiro.isTimeManual })}>{ritiro.isTimeManual ? <Lock className="h-3 w-3 text-[#1a5f5a]" /> : <Unlock className="h-3 w-3 text-slate-300" />}</button>
              </Label>
              <Input type="number" value={ritiro.tempoMin} onChange={(e) => upd({ tempoMin: parseFloat(e.target.value) || 0, isTimeManual: true })} className="mt-1" data-testid="ritiro-tempo-input" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 items-end">
            <div>
              <Label className="text-xs">Numero di persone</Label>
              <div className="flex items-center gap-2 mt-1">
                <Button size="icon" variant="outline" className="h-9 w-9" onClick={() => upd({ numPersone: Math.max(1, ritiro.numPersone - 1) })}><Minus className="h-3.5 w-3.5" /></Button>
                <span className="w-8 text-center font-semibold" data-testid="ritiro-persone">{ritiro.numPersone}</span>
                <Button size="icon" variant="outline" className="h-9 w-9" onClick={() => upd({ numPersone: Math.min(5, ritiro.numPersone + 1) })}><Plus className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
            <div>
              <Label className="text-xs">Tariffa oraria (€)</Label>
              <Input type="number" value={ritiro.tariffaOraria} onChange={(e) => upd({ tariffaOraria: parseFloat(e.target.value) || 0 })} className="mt-1" data-testid="ritiro-tariffa-input" />
            </div>
          </div>

          <div className="bg-slate-50 rounded-md p-4 text-sm space-y-1">
            <div className="flex justify-between text-slate-500"><span>Viaggio (A/R)</span><span>{ritiroBreakdown.travel} min</span></div>
            <div className="flex justify-between text-slate-500"><span>Facchinaggio</span><span>{ritiroBreakdown.fach} min</span></div>
            <div className="flex justify-between font-semibold text-slate-800 pt-2 border-t border-slate-200 mt-2">
              <span>Costo ritiro</span><span className="text-[#1a5f5a]" data-testid="ritiro-total">€{formatEur(ritiroTotal)}</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <div className="flex gap-2 items-center">
              <Input type="number" placeholder="Costo manuale €" value={manualCosto} onChange={(e) => setManualCosto(e.target.value)} className="w-36 h-9" />
              <Button size="sm" variant="outline" onClick={() => upd({ isCostoManual: true, costoTotaleManual: parseFloat(manualCosto) || 0 })} data-testid="ritiro-manual-cost">Imposta costo</Button>
              {ritiro.isCostoManual && <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => upd({ isCostoManual: false, costoTotaleManual: null })}>Auto</Button>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <Input type="number" placeholder="Facch. manuale (min)" value={manualFach} onChange={(e) => setManualFach(e.target.value)} className="w-40 h-9" />
            <Button size="sm" variant="outline" onClick={() => upd({ isFachManual: true, fachMinutesManual: parseFloat(manualFach) || 0 })}>Imposta facchinaggio</Button>
            {ritiro.isFachManual && <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => upd({ isFachManual: false, fachMinutesManual: null })}>Auto</Button>}
          </div>

          <div>
            <Label className="text-xs flex items-center gap-1">Nota / disclaimer (in corsivo nel messaggio)</Label>
            <Textarea value={ritiro.disclaimer ?? ""} onChange={(e) => upd({ disclaimer: e.target.value })}
              rows={3} className="mt-1 text-sm italic" data-testid="ritiro-disclaimer-input" />
          </div>
        </div>
      )}
    </div>
  );
}
